import { css } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { FormattedRelative } from '@dooer/react-intl'

import { useTheme } from '../../theme'

import { Flex } from '../flex'
import { StyledText } from '../styled-text'

import getStyles from './styles'

import Warning from './warning.svg'
import ArrowUp from './arrow-up.svg'

interface IActivityItemProps {
  children?: React.ReactNode
  last?: boolean
  level?: 'notice' | 'info' | 'warning' | 'critical'
  icon?: React.ReactNode
  isUnread?: boolean
}

export function ActivityItem({ last, level = 'info', children, icon, isUnread }: IActivityItemProps) {
  const theme = useTheme()
  const styles = getStyles(theme)

  return (
    <div {...css([styles.activityItem.base, last && styles.activityItem.last, isUnread && styles.activityItem.unread])}>
      <Flex row>
        <Flex alignContent="center" alignItems="center" flexShrink={0} style={{ ...styles.activityItem.iconWrapper }}>
          {icon != null ? (
            icon
          ) : level === 'notice' || level === 'info' ? (
            <ArrowUp />
          ) : level === 'warning' ? (
            <Warning fill={theme.colors.accents.alert} />
          ) : level === 'critical' ? (
            <Warning fill={theme.colors.accents.negative} />
          ) : null}

          {!last && <Flex flexGrow={1} style={{ ...styles.activityItem.connector }} />}
        </Flex>
        <Flex flexGrow={1} justifyContent="center">
          {children}
        </Flex>
      </Flex>
    </div>
  )
}
ActivityItem.propTypes = {
  children: PropTypes.node,
  last: PropTypes.bool,
  level: PropTypes.oneOf(['notice', 'info', 'warning', 'critical']),
  icon: PropTypes.node,
  isUnread: PropTypes.bool,
}

interface IActivitySubjectProps {
  children: React.ReactNode
  date: string | number | Date
}

export function ActivitySubject({ children, date }: IActivitySubjectProps) {
  return (
    <div {...css({ lineHeight: '20px' })}>
      {children}
      <StyledText variant={'dim'}>
        {' · '}
        <FormattedRelative value={date} />
      </StyledText>
    </div>
  )
}
ActivitySubject.propTypes = {
  children: PropTypes.node,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]).isRequired,
}

interface IActivityTextProps {
  children: React.ReactNode
  variant?: 'default' | 'dim' | 'alert'
}

export function ActivityText({ children, variant = 'default' }: IActivityTextProps) {
  const styles = getStyles(useTheme())

  return (
    <div
      {...css([
        styles.activityText.base,
        variant === 'alert' && styles.activityText.alert,
        variant === 'dim' && styles.activityText.dim,
      ])}
    >
      {children}
    </div>
  )
}
ActivityText.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'dim', 'alert']),
}
